import React, { FC, useMemo } from 'react';

import {
	EvFiltersPanelProvider,
	EvFlexColumn,
	EvFlexRow,
	EvLink,
	EvSection,
	EvSpinner,
	EvSwitch,
	EvTitle,
	TABLE_FILTERS_SR_SUMMARY,
	TEvFiltersPanelValues
} from '@evinced-private/ui-common';

import {
	MOBILE_SDK_DASHBOARD_PAGE_FILTERS_SECTION_ID,
	MOBILE_SDK_DASHBOARD_PAGE_MAIN_TITLE_ID,
	MOBILE_SDK_DASHBOARD_PAGE_OLDER_TESTS_LINK_ID,
	MOBILE_SDK_SHOW_ONLY_LATEST_TESTS_TOGGLE_ID
} from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import { getAllTestsTooltipText } from 'src/apps/mobile-sdk/helpers/MobileSdkDashboardHelpers';
import { MobileSdkTestsPageFilterDefinitions } from 'src/apps/mobile-sdk/helpers/MobileSdkTestsPageFilterDefinitionsHelper';
import { useMobileSdkFilterOptionsQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkFilterOptionsQuery';
import {
	PinnedDateRangeFiltersPanelBar,
	ProductFiltersPanelTrigger
} from 'src/common/components/pinned-date-range-filters-panel';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	buildPanelInitialValues,
	mapPanelValuesToFilters,
	mapToPanelDefinitions
} from 'src/common/helpers/FiltersPanelHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { MOBILE_SDK_TESTS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { showRecentTestKey } from 'src/common/providers/dashboard/TablesCustomStateHelper';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import {
	PLATFORM_OPTIONS,
	SEVERITY_OPTIONS,
	TESTS_PERIOD_OPTIONS
} from './MobileSdkTestsFilterDefinitions';
import { MOBILE_SDK_TESTS_PAGE_HEADER_LABELS } from './MobileSdkTestsPageHeader.labels';

interface MobileSdkTestsPageHeaderProps {
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	showRecentTest: boolean;
}

export const MobileSdkTestsPageHeader: FC<MobileSdkTestsPageHeaderProps> = ({
	filters,
	onFilterChange,
	showRecentTest
}) => {
	const { onTableParamStatusChange } = useTableState();
	const filterOptionsQuery = useMobileSdkFilterOptionsQuery();

	const panelDefinitions = useMemo(
		() => mapToPanelDefinitions(MobileSdkTestsPageFilterDefinitions),
		[]
	);

	const initialValues = useMemo<TEvFiltersPanelValues>(
		() => buildPanelInitialValues(panelDefinitions, filters),
		[panelDefinitions, filters]
	);

	const filterOptions = useMemo(() => {
		const staticFilterOptions = {
			platform: PLATFORM_OPTIONS,
			sessionsPeriod: TESTS_PERIOD_OPTIONS,
			severity: SEVERITY_OPTIONS
		};

		const dynamicFilterOptions = {
			sdkType: filterOptionsQuery.data?.sdkType ?? [],
			sdkVersion: filterOptionsQuery.data?.sdkVersion ?? [],
			appName: filterOptionsQuery.data?.appName ?? [],
			appVersion: filterOptionsQuery.data?.appVersion ?? []
		};

		return { ...staticFilterOptions, ...dynamicFilterOptions };
	}, [filterOptionsQuery.data]);

	const handleApply = (appliedFilters: TEvFiltersPanelValues): void => {
		onFilterChange(MOBILE_SDK_TESTS_TABLE_ID, mapPanelValuesToFilters(appliedFilters));
	};

	const { isLoading } = filterOptionsQuery;

	return (
		<EvFiltersPanelProvider
			appElement={PLATFORM_APP_ID}
			onApply={handleApply}
			onReset={handleApply}
			initialValues={initialValues}
			filterOptions={filterOptions}
			filterDefinitions={panelDefinitions}
		>
			<EvFlexColumn gap="XLARGE">
				<EvFlexRow align="CENTER" justify="SPACE_BETWEEN">
					<EvFlexRow align="CENTER" gap="MEDIUM" width="auto">
						<EvTitle
							titleText={MOBILE_SDK_TESTS_PAGE_HEADER_LABELS.PAGE_TITLE}
							dataAutomationId={MOBILE_SDK_DASHBOARD_PAGE_MAIN_TITLE_ID}
							headingLevel={1}
							className="main-title page-heading"
							MoreInfoContent={<>{getAllTestsTooltipText()}</>}
						/>
						<EvLink
							url={RoutesHelper.getMobileSdkV1DashboardPath()}
							linkText={MOBILE_SDK_TESTS_PAGE_HEADER_LABELS.OLDER_TESTS_LINK}
							showIcon
							dataAutomationId={MOBILE_SDK_DASHBOARD_PAGE_OLDER_TESTS_LINK_ID}
						/>
					</EvFlexRow>
					<EvFlexRow align="CENTER" gap="MEDIUM" width="auto">
						<EvSwitch
							dataAutomationId={MOBILE_SDK_SHOW_ONLY_LATEST_TESTS_TOGGLE_ID}
							onChange={() =>
								onTableParamStatusChange(
									MOBILE_SDK_TESTS_TABLE_ID,
									showRecentTestKey,
									() => !showRecentTest
								)
							}
							checked={showRecentTest}
							label={MOBILE_SDK_TESTS_PAGE_HEADER_LABELS.SHOW_ONLY_LATEST_TESTS}
						/>
						<ProductFiltersPanelTrigger />
					</EvFlexRow>
				</EvFlexRow>
				<EvSection
					ariaLabel={MOBILE_SDK_TESTS_PAGE_HEADER_LABELS.FILTERS_SECTION_ARIA_LABEL}
					ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
					ariaLive="polite"
					skipLinkId="filters-section"
					dataAutomationId={MOBILE_SDK_DASHBOARD_PAGE_FILTERS_SECTION_ID}
				>
					{!isLoading ? (
						<PinnedDateRangeFiltersPanelBar
							panelDefinitions={panelDefinitions}
							appliedValues={initialValues}
							onApply={handleApply}
						/>
					) : (
						<EvSpinner />
					)}
				</EvSection>
			</EvFlexColumn>
		</EvFiltersPanelProvider>
	);
};
